/**
 * Canonicalls continuity traversal — one system across surfaces.
 * Routes stay stable; spine provides coherent movement.
 */
import type { CrossPostPhase } from "./verifiedCrossPost";
import { normalizeCanonicallsPathname } from "./canonicallsHost";

export type CanonicallsTraversalId =
  | "origin"
  | "representation"
  | "verification"
  | "replay"
  | "actor"
  | "agreement"
  | "lineage";

export type CanonicallsTraversalStep = {
  id: CanonicallsTraversalId;
  label: string;
  shortLabel: string;
  description: string;
  to: string;
};

export const CANONICALLS_TRAVERSAL_SPINE: readonly CanonicallsTraversalStep[] = [
  {
    id: "origin",
    label: "Origin",
    shortLabel: "Origin",
    description: "Canonical source created by actor.",
    to: "/canonicalize",
  },
  {
    id: "representation",
    label: "Representation",
    shortLabel: "Represent",
    description: "Cross-surface representations generated.",
    to: "/canonicalize?traverse=representation",
  },
  {
    id: "verification",
    label: "Verification",
    shortLabel: "Verify",
    description: "Canonical lineage becomes inspectable.",
    to: "/inspect",
  },
  {
    id: "replay",
    label: "Replay",
    shortLabel: "Replay",
    description: "The source returns through continuity memory.",
    to: "/replay",
  },
  {
    id: "actor",
    label: "Actor continuity",
    shortLabel: "Actor",
    description: "Persistent identity survives traversal.",
    to: "/aether",
  },
  {
    id: "agreement",
    label: "Agreement continuity",
    shortLabel: "Agreement",
    description: "Obligations and destinations attach to continuity.",
    to: "/aether#agreement",
  },
  {
    id: "lineage",
    label: "Inspectable lineage",
    shortLabel: "Lineage",
    description: "Movement across surfaces remains inspectable.",
    to: "/inspect#lineage",
  },
] as const;

/** Canonicalize sub-flow maps to the first four spine steps. */
export const CANONICALLS_CANONICALIZE_FLOW = ["Origin", "Representation", "Verification", "Replay"] as const;

export function canonicallsPhaseFromTraverseSlug(
  slug: string | null | undefined,
): CrossPostPhase | null {
  const s = (slug || "").trim().toLowerCase();
  if (!s || s === "origin" || s === "draft") return "compose";
  if (s === "canonicalize" || s === "confirm" || s === "preserve") return "confirm";
  if (s === "representation" || s === "represent" || s === "crosspost") return "crosspost";
  if (s === "verification" || s === "verify") return "verify";
  return null;
}

export function canonicallsTraverseSlugForPhase(phase: CrossPostPhase): string {
  if (phase === "compose") return "origin";
  if (phase === "confirm" || phase === "preserve") return "canonicalize";
  if (phase === "crosspost") return "representation";
  return "verification";
}

export function canonicallsFlowStepIndex(phase: CrossPostPhase): number {
  if (phase === "compose") return 0;
  if (phase === "confirm" || phase === "preserve") return 0;
  if (phase === "crosspost") return 1;
  if (phase === "verify") return 2;
  return 3;
}

export function normalizeCanonicallsPath(pathname: string): string {
  return normalizeCanonicallsPathname(pathname);
}

export function resolveCanonicallsTraversalStep(
  pathname: string,
  hash = "",
): CanonicallsTraversalStep | null {
  const p = normalizeCanonicallsPath(pathname);
  const h = hash.replace(/^#/, "").toLowerCase();

  if (p === "/") return CANONICALLS_TRAVERSAL_SPINE[0];
  if (p === "/canonicalize") return CANONICALLS_TRAVERSAL_SPINE[0];
  if (p === "/replay") return CANONICALLS_TRAVERSAL_SPINE[3];
  if (p === "/aether") {
    return h === "agreement" ? CANONICALLS_TRAVERSAL_SPINE[5] : CANONICALLS_TRAVERSAL_SPINE[4];
  }
  if (p === "/inspect") {
    return h === "lineage" ? CANONICALLS_TRAVERSAL_SPINE[6] : CANONICALLS_TRAVERSAL_SPINE[2];
  }
  return null;
}

export function resolveCanonicallsTraversalStepWithPhase(
  pathname: string,
  search: string,
  hash = "",
): CanonicallsTraversalStep | null {
  const p = normalizeCanonicallsPath(pathname);
  if (p === "/canonicalize") {
    const slug = new URLSearchParams(search).get("traverse");
    const phase = canonicallsPhaseFromTraverseSlug(slug);
    if (phase === "crosspost") return CANONICALLS_TRAVERSAL_SPINE[1];
    if (phase === "verify") return CANONICALLS_TRAVERSAL_SPINE[2];
    if (phase === "confirm" || phase === "preserve") return CANONICALLS_TRAVERSAL_SPINE[0];
    return CANONICALLS_TRAVERSAL_SPINE[0];
  }
  return resolveCanonicallsTraversalStep(pathname, hash);
}

export function traversalStepIndex(id: CanonicallsTraversalId): number {
  return CANONICALLS_TRAVERSAL_SPINE.findIndex((s) => s.id === id);
}


export function traversalStepRank(id: string | null | undefined): number {
  if (!id) return -1;
  const idx = CANONICALLS_TRAVERSAL_SPINE.findIndex((s) => s.id === id);
  return idx >= 0 ? idx : -1;
}

/** Prefer the farther-along spine step (never downgrade carry for URL lag). */
export function preferAheadTraversalStep(
  urlStepId: string | null | undefined,
  carryStepId: string | null | undefined,
): CanonicallsTraversalId | string {
  const urlRank = traversalStepRank(urlStepId);
  const carryRank = traversalStepRank(carryStepId);
  if (urlRank < 0 && carryRank < 0) return "origin";
  if (urlRank < 0) return carryStepId ?? "origin";
  if (carryRank < 0) return urlStepId ?? "origin";
  return urlRank >= carryRank ? (urlStepId as CanonicallsTraversalId) : (carryStepId as CanonicallsTraversalId);
}
